// حوزه اجرایی قراردادها
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge } from '../../components/paging';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);

const EMPTY = { code: '', title: '', leader_title: '', people_id: 0, leader_name: '' };

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

function Lookup({ label, value, endpoint, onPick, flex = 1 }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}?q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => search(q)} onChange={e => search(e.target.value)} placeholder="جستجو..." style={ui.input} />
      {value && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ExecutiveDomainList({ user, onBack }) {
  // دکمه‌های فرم FRMEXECUTIVEDOMAIN — دیدن دکمه = داشتن مجوز
  const vbf = useFormButtons('FRMEXECUTIVEDOMAIN', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const [data, setData]         = useState([]);
  const pg = usePager();
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const cols = ['کد', 'عنوان', 'عنوان معاونت', 'نام معاون'];
  const initW = [90, 260, 220, 220];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async (sr = search, page = 1) => {
    setLoading(true); setError(''); if (page === 1) setSelected(null);
    const j = await API(`/executive-domains/?search=${encodeURIComponent(sr)}&page=${page}`);
    setLoading(false);
    if (j.ok) setData(prev => pg.merge(prev, j, page)); else setError(j.msg);
  }, [search, pg]);

  useEffect(() => { load(''); }, []);   // eslint-disable-line

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY); setError(''); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return; if (!selected) return; setForm({ ...EMPTY, ...selected }); setError(''); setModal('edit'); };
  const openDel  = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    setSaving(true); setError('');
    const j = modal === 'new'
      ? await API('/executive-domains/create/', { method: 'POST', body: JSON.stringify(form) })
      : await API(`/executive-domains/${selected.id}/update/`, { method: 'PUT', body: JSON.stringify(form) });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/executive-domains/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal(null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(search)}
        style={{ ...ui.searchInput, flex: 1, minWidth: '180px' }} placeholder="جستجو در عنوان..." />
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pg.total} loading={loading} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="حوزه اجرایی قراردادها" subtitle="بخش قراردادها — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}
        pager={pg} loading={loading} onMore={p => load(search, p)}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf6e3';
              const tc  = sel ? '#fff' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, color: tc, fontFamily: 'monospace' }}>{toFa(r.code)}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.title || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.leader_title || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.leader_name || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 حوزه اجرایی یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ حوزه اجرایی جدید' : '✎ ویرایش'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '120px' }}>
                  <label style={ui.mlabel}>کد</label>
                  <input type="number" value={form.code} onChange={e => F('code', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>عنوان</label>
                  <input value={form.title} onChange={e => F('title', e.target.value)} style={ui.input} autoFocus />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>عنوان معاونت</label>
                  <input value={form.leader_title} onChange={e => F('leader_title', e.target.value)} style={ui.input} />
                </div>
              </div>
              <div style={ui.row}>
                <Lookup label="نام معاون" value={form.leader_name} endpoint="/executive-domains/lookup/people/"
                  onPick={r => setForm(f => ({ ...f, people_id: r.id, leader_name: r.display }))} />
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              <div style={{ fontSize: '34px', marginBottom: '10px' }}>🗑️</div>
              {selected?.title}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
